import { useEffect, useState } from "react";
import { passCurrencySymbol } from "../../utils/currencyFormat";
import PolicyModal from "./PolicyModal";

export default function PurchaseModal({
  showPurchaseModal,
  setShowPurchaseModal,
  selectedClass,
  setSelectedClass,
  language,
  details,
  getFilteredPassTypesByLocation,
  selectedPass,
  setSelectedPass,
  isSubscription,
  setIsSubscription,
  hasActiveSubscription,
  getActiveDiscount,
  handlePurchasePass,
  translatePassName,
  showRulesCheckbox = false,
  rulesAgreedChecked = false,
  setRulesAgreedChecked,
  policyTitle,
  policyContent,
  isLoggedIn = true,
}) {
  const [policyViewerOpen, setPolicyViewerOpen] = useState(false);
  const rulesModalTitle = policyTitle ?? details.firstPassRulesTitle;
  const rulesModalContent =
    policyContent ?? details.firstPassRulesContent ?? "";
  // When modal opens, ensure selectedPass is one of the available (filtered) options
  useEffect(() => {
    if (!showPurchaseModal) return;
    const available = getFilteredPassTypesByLocation();
    const isSelectedAvailable = selectedPass && available.some((p) => p._id === selectedPass._id);
    if (!isSelectedAvailable && available.length > 0) {
      setSelectedPass(available[0]);
    }
  }, [showPurchaseModal]);

  useEffect(() => {
    if (!showPurchaseModal) setPolicyViewerOpen(false);
  }, [showPurchaseModal]);

  // Subscription-only pass types have no one-time option, so force the subscription choice.
  useEffect(() => {
    if (!showPurchaseModal) return;
    if (selectedPass?.subscription?.required) setIsSubscription(true);
  }, [showPurchaseModal, selectedPass?._id, selectedPass?.subscription?.required]);

  if (!showPurchaseModal) return null;

  return (
    <div 
      className="fixed inset-0 bg-black bg-opacity-50 flex h-screen items-center justify-center z-50 transition-opacity duration-300"
      onClick={(e) => {
        if (e.target === e.currentTarget) {
          setShowPurchaseModal(false);
          setSelectedClass(null);
          setIsSubscription(false);
        }
      }}
      style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0 }}
    >
      <div 
        className="bg-white  md:rounded-xl w-full md:w-96 max-h-[90vh] md:max-h-none min-h-0 overflow-y-auto overflow-x-hidden p-4 md:p-6 relative transform transition-all duration-500 ease-out"
        style={{ maxHeight: '100vh', WebkitOverflowScrolling: 'touch' }}
      >
        <button
          className="absolute top-1 right-1 text-gray-500 hover:text-gray-700 text-sm font-bold"
          onClick={() => {
            setShowPurchaseModal(false);
            setSelectedClass(null);
            setIsSubscription(false);
          }}
          type="button"
        >
          ✕
        </button>
        
        {/* <h2 className="text-base font-bold mb-4">
          {details.selectPass || "Select a Pass"}
        </h2> */}
        
        {/* Pass Selection */}
        <div className="space-y-2 mb-4">
          {getFilteredPassTypesByLocation().map((pass) => {
            const activeDiscount = getActiveDiscount(pass);
            const discountedPrice = activeDiscount 
              ? Math.round(pass.basePrice * (1 - activeDiscount.discountPercent / 100))
              : pass.basePrice;
            const curSym = passCurrencySymbol(pass.currency || "ILS");
            
            return (
              <button
                key={pass._id}
                onClick={() => setSelectedPass(pass)}
                className={`w-full p-3 rounded-xl text-left border-2 transition ${
                  selectedPass?._id === pass._id
                    ? "border-brown bg-brown bg-opacity-10"
                    : "border-gray-300 hover:border-brown"
                }`}
                type="button"
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <p className="font-semibold text-xs">{translatePassName(pass)}</p>
                    <p className="text-xs text-gray-600">
                      {language === "russian" ? pass.descriptionRu : pass.descriptionEn}
                    </p>
                  </div>
                  {/* {activeDiscount && (
                    <span className="text-xs bg-pink text-white px-2 rounded-full ml-2 whitespace-nowrap">
                      -{activeDiscount.discountPercent}%
                    </span>
                  )} */}
                </div>
                <div className="flex items-center gap-2">
                  <p className={`font-bold text-xs ${activeDiscount ? 'text-gray-400 line-through' : 'text-brown'}`}>
                    {pass.basePrice} {curSym}
                  </p>
                  {activeDiscount && (
                    <p className="text-pink font-bold text-xs">
                      {discountedPrice} {curSym}
                    </p>
                  )}
                </div>
              </button>
            );
          })}
        </div>
        
        {selectedPass && (() => {
          const selectedCurSym = passCurrencySymbol(selectedPass.currency || "ILS");
          return (
          <>
            {hasActiveSubscription() && (
              <div className="mb-4 p-3 bg-blue-100 w-fit mx-auto rounded-lg border border-blue-300">
                <p className="text-blue-700 text-xs font-semibold">
                  ℹ️ {details.activeSubscriptionMessage}
                </p>
              </div>
            )}

            {selectedPass.subscription?.enabled && (
              <>
                {/* Purchase Type Selection - ONLY show if user doesn't have active subscription */}
                {!hasActiveSubscription() && (
                  <>
                    <div className="mb-4 space-y-2">
                      {/* One-time Purchase */}
                      {!selectedPass.subscription?.required && (
                        <label className="block cursor-pointer">
                          <input
                            type="radio"
                            name="purchaseType"
                            checked={!isSubscription}
                            onChange={() => setIsSubscription(false)}
                            className="hidden"
                          />
                          <div className={`p-3 rounded-xl border-2 transition ${
                            !isSubscription
                              ? 'border-brown bg-brown bg-opacity-10'
                              : 'border-gray-300 hover:border-brown'
                          }`}>
                            <div className="flex items-start gap-3">
                              <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center mt-0.5 ${
                                !isSubscription ? 'border-brown bg-brown' : 'border-gray-300'
                              }`}>
                                {!isSubscription && <div className="w-2 h-2 bg-white rounded-full" />}
                              </div>
                              <div className="flex-1">
                                <p className="font-semibold text-sm">{details.oneTimePurchase}</p>
                                <p className="text-xs text-gray-600">{details.validForMonth}</p>
                                <div className="flex items-center gap-2 mt-1 w-fit mx-auto">
                                  <p className={`text-sm font-bold ${getActiveDiscount(selectedPass) ? 'text-gray-400 line-through' : 'text-brown'}`}>
                                    {Math.round(selectedPass.basePrice)} {selectedCurSym}
                                  </p>
                                  {getActiveDiscount(selectedPass) && (
                                    <p className="text-pink font-bold text-sm">
                                      {Math.round(selectedPass.basePrice * (1 - getActiveDiscount(selectedPass).discountPercent / 100))} {selectedCurSym}
                                    </p>
                                  )}
                                </div>
                              </div>
                            </div>
                          </div>
                        </label>
                      )}

                      {/* Subscription */}
                      <label className="block cursor-pointer">
                        <input
                          type="radio"
                          name="purchaseType"
                          checked={isSubscription}
                          onChange={() => setIsSubscription(true)}
                          className="hidden"
                        />
                        <div className={`p-3 rounded-xl border-2 transition ${
                          isSubscription
                            ? 'border-orange-400 bg-orange-50'
                            : 'border-gray-300 hover:border-pink'
                        }`}>
                          <div className="flex items-start gap-3 ">
                            <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center mt-0.5 ${
                              isSubscription ? 'border-orange-400 bg-pink' : 'border-gray-300'
                            }`}>
                              {isSubscription && <div className="w-2 h-2 bg-white rounded-full" />}
                            </div>
                            <div className="flex-1">
                              <div className="flex items-center gap-2 w-fit mx-auto">
                                <p className="font-semibold text-sm">{details.subscribe}</p>
                                <span className="text-xs bg-pink text-white px-2 py-1 rounded-full">
                                  -{selectedPass.subscription.monthlyPrice
                                    ? Math.round(100 - (selectedPass.subscription.monthlyPrice / selectedPass.basePrice) * 100)
                                    : selectedPass.subscription.discountPercent}%
                                </span>
                              </div>
                              <p className="text-xs text-gray-600">{details.autoRenewsMonthly}</p>
                              <p className="text-sm font-bold text-pink mt-1">
                                {Math.round(selectedPass.subscription.monthlyPrice || (selectedPass.basePrice * (1 - selectedPass.subscription.discountPercent / 100)))} ₪/{details.month || "month"}
                              </p>
                            </div>
                          </div>
                        </div>
                      </label>
                    </div>
                  </>
                )}
                
                {/* Subscription Info */}
                {isSubscription && (
                  <p className="text-[10px] text-gray-600 mb-4 leading-tight bg-blue-50 p-2 rounded-lg">
                    {selectedPass.subscription?.required ? `ℹ️ ${details.subscriptionOnlyNotice} ` : '💡 '}
                    {details.subscriptionInfo}
                  </p>
                )}
              </>
            )}

            {/* Logged in + first pass: require checkbox */}
            {isLoggedIn && showRulesCheckbox && (
              <label className="flex items-start gap-3 mb-4 p-3 rounded-xl border-2 border-gray-300 hover:border-brown cursor-pointer">
                <input
                  type="checkbox"
                  checked={rulesAgreedChecked}
                  onChange={(e) => setRulesAgreedChecked?.(e.target.checked)}
                  className="mt-1 w-4 h-4 rounded border-gray-300 text-brown focus:ring-brown"
                />
                <span className="text-xs text-gray-700">
                  {details.firstPassRulesLabelPrefix}
                  <span
                    role="button"
                    tabIndex={0}
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      setPolicyViewerOpen(true);
                    }}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        setPolicyViewerOpen(true);
                      }
                    }}
                    className="text-brown font-semibold underline hover:no-underline focus:outline-none cursor-pointer inline"
                  >
                    {details.firstPassRulesLinkText}
                  </span>
                  {details.firstPassRulesLabelSuffix}
                </span>
              </label>
            )}

            {/* Logged out: show Rules and policies link only (no "I have read" prefix); Buy pass will redirect to login */}
            {!isLoggedIn && details.rulesAndPoliciesLink && (
              <p className="text-xs text-gray-700 mb-4">
                <span
                  role="button"
                  tabIndex={0}
                  onClick={(e) => {
                    e.preventDefault();
                    setPolicyViewerOpen(true);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      setPolicyViewerOpen(true);
                    }
                  }}
                  className="text-brown font-semibold underline hover:no-underline focus:outline-none cursor-pointer inline"
                >
                  {details.rulesAndPoliciesLink}
                </span>
              </p>
            )}

            <button
              className="w-full bg-brown text-white rounded-xl py-2 font-semibold text-sm hover:bg-beige hover:text-brown transition disabled:opacity-50 disabled:cursor-not-allowed"
              onClick={handlePurchasePass}
              type="button"
              disabled={isLoggedIn && showRulesCheckbox && !rulesAgreedChecked}
            >
              {isLoggedIn ? details.confirmPurchase : (details.logIn || "Log in")}
            </button>
          </>
          );
        })()}
      </div>

      <PolicyModal
        open={policyViewerOpen}
        onClose={() => setPolicyViewerOpen(false)}
        title={rulesModalTitle}
        content={rulesModalContent}
      />
    </div>
  );
}